/**
 * The notes themselves, printed under the document the way they would be on a
 * page.
 *
 * Purely derived from the source — the note text lives inline in the document
 * as `^[…]`, so there is nothing to edit here that isn't also visible where it
 * was written. Clicking an entry selects its marker in the text rather than
 * opening a second editing surface that could drift from the first.
 *
 * The list is handed down as a prop rather than derived here. Rescanning the
 * document on every keystroke is exactly the cost the editor is built to avoid;
 * the page recomputes it on the same debounce that already handles word count
 * and the outline.
 */

import { escapeHtml } from "@/lib/editor";
import type { FootnoteEntry } from "./extensions/footnote-markdown";

/**
 * Render the inline markdown a citation actually uses — italic titles, the
 * occasional bold, code for a reference.
 *
 * Deliberately hand-rolled rather than run through `marked`: the input is
 * escaped first and only our own tags are introduced afterwards, so there is
 * no path from note text to live markup. A full markdown renderer here would
 * need sanitising to be equally safe.
 */
function renderInline(text: string): string {
  return escapeHtml(text)
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/(^|[^*])\*([^*]+)\*/g, "$1<em>$2</em>")
    .replace(/(^|[^_])_([^_]+)_/g, "$1<em>$2</em>");
}

interface FootnoteListProps {
  notes: FootnoteEntry[];
  onSelect: (note: FootnoteEntry) => void;
}

export function FootnoteList({ notes, onSelect }: FootnoteListProps) {
  if (notes.length === 0) return null;

  return (
    <section className="footnote-list mt-16 border-t border-border pt-6">
      <h2 className="sr-only">Footnotes</h2>
      <ol className="space-y-2 text-sm text-muted-foreground">
        {notes.map((note) => (
          <li key={note.pos} className="leading-relaxed">
            <button
              type="button"
              onClick={() => onSelect(note)}
              className="text-left hover:text-foreground transition-colors"
              title="Go to this footnote in the text"
              dangerouslySetInnerHTML={{ __html: renderInline(note.content) }}
            />
          </li>
        ))}
      </ol>
    </section>
  );
}
